import { useState } from "react";
import { toast } from "sonner";
import { ChatCircleText } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useNateM } from "../../context/NateMContext";

export function FloatingMessageButton() {
  const { user } = useAuth();
  const { w9Gated } = useNateM();
  const isRep = user?.role === "affiliate";
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  if (w9Gated) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      await api.post("/client/message", { message });
      toast.success(isRep ? "Got it — I've sent a message to your manager. You'll hear back shortly." : "Message sent to the Back Office — our team will respond shortly.");
      setMessage("");
      setOpen(false);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="floating-send-message-button"
        onClick={() => setOpen(true)}
        title="Send a message to your manager or the back office"
        className="flex items-center gap-2 px-4 h-10 rounded-full bg-igc-gold hover:bg-igc-goldHover text-black shadow-md hover:-translate-y-0.5 transition-all shrink-0"
      >
        <ChatCircleText size={18} weight="duotone" />
        <span className="hidden sm:inline text-sm font-medium whitespace-nowrap">{isRep ? "Contact My Manager" : "Message Back Office"}</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="send-message-modal">
          <DialogHeader><DialogTitle className="font-serif">{isRep ? "Contact My Manager" : "Message Back Office"}</DialogTitle></DialogHeader>
          {isRep && <p className="text-igc-muted text-sm">Hey, what's going on? Tell me what you need and I'll make sure your manager gets it.</p>}
          <form onSubmit={submit} className="space-y-3">
            <Textarea
              data-testid="send-message-textarea"
              required
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              title={isRep ? "Your message goes straight to your manager." : "Your message goes straight to the Back Office team."}
              className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream min-h-32"
            />
            <Button data-testid="send-message-submit-button" type="submit" disabled={sending} title="Sends this message." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">
              {sending ? "Sending..." : "Send Message"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
